// --- START OF FILE authHelper.js ---

class AuthHelper {
  constructor() {
    this.JABA_JABA = "$3c6e7K(y2o24!@#$%^&*()_+Secure@pp";
    this.HARDCODED_KEY = "$3c6e7K(y2o24!@#$%^&*()_+Secure@pp";

    this.removeOldKeys();
  }

  init = () => {
    this.blockInsecureStorage();
    this.setupInactivityTimeout();
    this.removeOldKeys(); // Ensure old, insecure keys are removed on init
  };

  getBrowserSignature = () => {
    try {
      const browserData = [
        window.navigator.language || "unknown",
        window.screen.width + "x" + window.screen.height,
        window.navigator.platform || "unknown",
        window.navigator.userAgent.substring(0, 50) || "unknown",
        window.screen.colorDepth || "24",
        window.screen.pixelDepth || "24",
        new Date().getTimezoneOffset().toString(),
        window.navigator.hardwareConcurrency || "4",
        window.navigator.maxTouchPoints || "0",
        window.navigator.cookieEnabled ? "cookies" : "nocookies",
        window.navigator.onLine ? "online" : "offline",
        window.screen.orientation ? window.screen.orientation.type : "unknown",
        window.devicePixelRatio || "1",
        this.getCanvasFingerprint(),
        this.getWebGLFingerprint(),
      ].join("|");

      return btoa(this.HARDCODED_KEY + "|" + browserData);
    } catch (error) {
      console.error("Error getting browser signature:", error);
      return btoa(this.HARDCODED_KEY + "|f@!L6AcK-k3Y_F0rR~B60w5er");
    }
  };

  getCanvasFingerprint = () => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("Hello, user.", 2, 15);
      return canvas.toDataURL();
    } catch (error) {
      console.error("Error getting canvas fingerprint:", error);
      return "canvas-unsupported";
    }
  };

  getWebGLFingerprint = (() => {
    let cachedFingerprint = null;

    return () => {
      if (cachedFingerprint) return cachedFingerprint;

      try {
        const canvas = document.createElement("canvas");
        const gl =
          canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
        if (!gl) {
          cachedFingerprint = "webgl-unsupported";
          return cachedFingerprint;
        }

        const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
        cachedFingerprint = debugInfo
          ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) +
            "|" +
            gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
          : "webgl-info-unavailable";

        const loseCtx = gl.getExtension("WEBGL_lose_context");
        if (loseCtx) loseCtx.loseContext();

        return cachedFingerprint;
      } catch (error) {
        console.error("Error getting WebGL fingerprint:", error);
        cachedFingerprint = "webgl-error";
        return cachedFingerprint;
      }
    };
  })();

  simpleEncrypt = (text, key) => {
    const randomPrefix = Math.random().toString(36).substring(2, 8);
    const randomSuffix = Math.random().toString(36).substring(2, 8);
    const textToEncrypt = randomPrefix + text + randomSuffix;
    let result = "";
    for (let i = 0; i < textToEncrypt.length; i++) {
      const encryptedChar =
        textToEncrypt.charCodeAt(i) ^ key.charCodeAt(i % key.length);
      result += encryptedChar.toString(16).padStart(2, "0");
    }
    const prefixLength = randomPrefix.length.toString(16).padStart(2, "0");
    const suffixLength = randomSuffix.length.toString(16).padStart(2, "0");

    return prefixLength + suffixLength + result;
  };

  simpleDecrypt = (encryptedHex, key) => {
    try {
      const prefixLength = parseInt(encryptedHex.substr(0, 2), 16);
      const suffixLength = parseInt(encryptedHex.substr(2, 2), 16);
      const dataHex = encryptedHex.substr(4);
      let decrypted = "";

      for (let i = 0; i < dataHex.length; i += 2) {
        const hexPair = dataHex.substr(i, 2);
        const encryptedChar = parseInt(hexPair, 16);
        const originalChar =
          encryptedChar ^ key.charCodeAt((i / 2) % key.length);
        decrypted += String.fromCharCode(originalChar);
      }
      const actualData = decrypted.substring(
        prefixLength,
        decrypted.length - suffixLength,
      );
      return actualData;
    } catch (error) {
      console.error("Error decrypting session data:", error);
      return null;
    }
  };

  setLogin = (userData) => {
    const sessionData = {
      userId: userData.userId,
      role: userData.role,
      siteId: userData.siteId,
      loginTime: Date.now(),
      nonce: Math.random().toString(36).substring(2, 15),
    };
    const browserKey = this.getBrowserSignature();
    const sessionJson = JSON.stringify(sessionData);
    const encryptedSession = this.simpleEncrypt(sessionJson, browserKey);

    localStorage.setItem("authSession", encryptedSession);
    localStorage.setItem("lastActivity", Date.now().toString());

    // Call init to ensure storage blocking and inactivity timeout are set
    // This is crucial for every successful login
    this.init();
  };

  isAuthenticated = () => {
    try {
      const encryptedSession = localStorage.getItem("authSession");
      if (!encryptedSession) {
        return false;
      }
      const browserKey = this.getBrowserSignature();
      const decryptedJson = this.simpleDecrypt(encryptedSession, browserKey);
      if (!decryptedJson) {
        return false;
      }
      const session = JSON.parse(decryptedJson);

      // Also ensure we have a valid refresh token in localStorage to be considered fully authenticated
      // If refreshToken is going to be in an HttpOnly cookie, this check will change.
      // For now, it remains in localStorage, so we check for its presence.
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken || !(session && session.userId)) {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking authentication status:", error);
      return false;
    }
  };

  getUserData = () => {
    if (!this.isAuthenticated()) {
      return null;
    }

    try {
      const encryptedSession = localStorage.getItem("authSession");
      const browserKey = this.getBrowserSignature();
      const decryptedJson = this.simpleDecrypt(encryptedSession, browserKey);

      if (!decryptedJson) return null;

      const session = JSON.parse(decryptedJson);
      return {
        userId: session.userId,
        role: session.role,
        siteId: session.siteId,
      };
    } catch (error) {
      console.error("Error getting user data:", error);
      return null;
    }
  };

  getUserId = () => {
    const userData = this.getUserData();
    return userData ? userData.userId : null;
  };

  getRole = () => {
    const userData = this.getUserData();
    return userData ? userData.role : null;
  };

  getSiteId = () => {
    const userData = this.getUserData();
    return userData ? userData.siteId : null;
  };

  isLoggedIn = () => {
    return this.isAuthenticated();
  };

  hasRole = (requiredRole) => {
    const role = this.getRole();
    return role === requiredRole;
  };

  clearSessionData = () => {
    localStorage.removeItem("authSession");
    localStorage.removeItem("lastActivity");
    localStorage.removeItem("authToken"); // Keep this for now to ensure old values are cleared
    localStorage.removeItem("refreshToken"); // Keep this for now to ensure old values are cleared
    // localStorage.clear // This clears EVERYTHING, often too aggressive.
    localStorage.removeItem("medicine_branch_id");
    this.removeOldKeys();
  };

  logout = (redirect = true) => {
    this.clearSessionData();
    if (redirect) {
      const currentPath = window.location.pathname;
      if (!currentPath.includes("login")) {
        // Prevent redirect loop if already on login page
        window.location.href = "/";
      }
    }
  };

  // This removes any old, less secure keys from localStorage
  removeOldKeys = () => {
    ["isLogin", "userId", "role"].forEach((key) => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        window.sessionStorage?.setItem("isLogin", false);
      }
    });
  };

  // Prevents direct setting of sensitive keys if any old code tries to
  blockInsecureStorage = () => {
    const blockedKeys = ["isLogin", "userId", "role"]; // These should only be stored encrypted in 'authSession'
    // Do NOT block 'authToken' or 'refreshToken' here as apiService will manage them.

    const originalSetItem = localStorage.setItem;

    localStorage.setItem = function (key, value) {
      if (blockedKeys.includes(key)) {
        console.error(
          `Attempted to set blocked localStorage key: ${key}. Action prevented.`,
        );
        return; // Prevent setting these keys
      }
      originalSetItem.apply(localStorage, [key, value]);
    };
  };

  setupInactivityTimeout = () => {
    // Ensure this runs only in browser environment
    if (typeof window === "undefined" || !window.document) {
      return;
    }

    // Clear any existing interval to prevent multiple timers running
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
    }

    const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    const CHECK_INTERVAL_MS = 2 * 60 * 1000; // Check every 2 minutes

    let lastActivity = Date.now(); // Initialize with current time on setup

    const updateActivity = () => {
      lastActivity = Date.now();
      if (this.isAuthenticated()) {
        try {
          localStorage.setItem("lastActivity", lastActivity.toString());
        } catch (error) {
          console.error("Error updating last activity in localStorage:", error);
        }
      }
    };

    const events = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    events.forEach((event) => {
      try {
        window.document.addEventListener(event, updateActivity, {
          passive: true,
        });
      } catch (error) {
        console.error(`Error adding event listener for ${event}:`, error);
      }
    });

    this.inactivityInterval = setInterval(() => {
      // Check if user is still considered authenticated by authHelper
      // If not, clear the interval and stop checking
      if (!this.isAuthenticated()) {
        clearInterval(this.inactivityInterval);
        this.inactivityInterval = null; // Clear reference
        return;
      }

      try {
        const storedActivity = localStorage.getItem("lastActivity");
        const lastActivityTime = storedActivity
          ? parseInt(storedActivity, 10)
          : lastActivity;
        const inactiveTime = Date.now() - lastActivityTime;

        if (inactiveTime > INACTIVITY_TIMEOUT_MS) {
          clearInterval(this.inactivityInterval);
          this.inactivityInterval = null; // Clear reference
          this.logout(true); // Force logout and redirect
        }
      } catch (error) {
        console.error("Error during inactivity check:", error);
        // If localStorage access fails, assume session is compromised or broken and log out.
        clearInterval(this.inactivityInterval);
        this.inactivityInterval = null; // Clear reference
        this.logout(true);
      }
    }, CHECK_INTERVAL_MS); // Check every 2 minutes
  };

  requireAuth = () => {
    return this.isAuthenticated(); // Simplified: just checks if authenticated
  };

  // This `updateActivity` is for external use if needed, but the internal one handles events.
  // The `setupInactivityTimeout` already adds event listeners, so explicit calls might be redundant.
  // Leaving it here as it was in your original code.
  updateActivity = () => {
    if (this.isAuthenticated()) {
      localStorage.setItem("lastActivity", Date.now().toString());
    }
  };
}

const authHelper = new AuthHelper();

// Initialize authHelper on script load to set up security measures
authHelper.init();

// Utility functions
const getUserId = () => authHelper.getUserId();
const getRole = () => authHelper.getRole();
const getSiteId = () => authHelper.getSiteId();
const isLoggedIn = () => authHelper.isLoggedIn();
const requireAuth = () => authHelper.requireAuth();
const logout = () => authHelper.logout();
const hasRole = (role) => authHelper.hasRole(role);

// Added missing function - this is mainly for internal use by authHelper.setLogin now.
// External calls to setIsAuthenticated(false) should instead use authHelper.logout().
const setIsAuthenticated = (isAuthenticated) => {
  if (!isAuthenticated) {
    authHelper.clearSessionData();
  }
};

export {
  authHelper,
  getUserId,
  getRole,
  getSiteId,
  isLoggedIn,
  requireAuth,
  logout,
  hasRole,
  setIsAuthenticated,
};

export const clearAuthData = () => {
  // Clear all authentication-related data, including from apiService.js's localStorage
  localStorage.removeItem("authSession");
  localStorage.removeItem("lastActivity");
  localStorage.removeItem("authToken"); // Ensure old keys are cleared
  localStorage.removeItem("refreshToken"); // Ensure old keys are cleared
  localStorage.removeItem("userData"); // Ensure old keys are cleared
  sessionStorage.removeItem("authToken"); // Ensure old keys are cleared
  sessionStorage.removeItem("userData"); // Ensure old keys are cleared
  sessionStorage.removeItem("refreshToken"); // Ensure old keys are cleared

  // Also clear the old keys that might still exist
  ["isLogin", "userId", "role"].forEach((key) => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
    if (sessionStorage.getItem(key)) {
      sessionStorage.removeItem(key);
    }
  });
};
